// Function index:
//   MountWorldDeps     — config object the orchestrator hands in.
//   mountWorld(deps)   — per-world setup: scene (hex OR room-graph),
//       interior indicator, Enter prompt, hex-streaming wire-up,
//       multi-character chip live updates. Returns a teardown function
//       runGame invokes before remounting for a new world.
//   isOnOrAdjacent(lq, lr, q, r) — axial-distance ≤ 1 test.

// The boundary: anything inside this file is per-world (rebuilt on
// character.world.changed). The party-scoped surface (chat / banner /
// WS / journal / strategic map) stays alive in runtime.ts.

import { mountHexWorld } from './mount-hex.js';
import { mountRoomWorld } from './mount-room.js';
import { installWorldUi } from './world-ui.js';
import type { ChatMount } from '../chat.js';
import type { BannerHandle } from '../combat-banner.js';
import type { RulesPauseBannerHandle } from '../rules-pause-banner.js';
import type { WS } from '../ws.js';
import type { BootstrapResponse } from '../api.js';
import type { SceneMount } from '../hex-scene/types.js';
import type { HexHistoryOverlay } from './hex-history-overlay.js';
import { bindGameWS } from './ws-bindings.js';

const ROOM_GRAPH_KINDS = new Set(['tower', 'labyrinth', 'dungeon']);

export interface MountWorldDeps {
  appEl: HTMLElement;
  partyId: number;
  boot: BootstrapResponse;
  ws: WS;
  chat: ChatMount;
  banner: BannerHandle;
  pauseBanner: RulesPauseBannerHandle;
  isPaused: () => boolean;
  selfCharId: number | null;
  charNames: Map<number, string>;
  npcNames: Map<number, string>;
  onSelfHpChange: (hp: number, max_hp: number) => void;
  onCombatChange: () => void;
  partyLabel: HTMLElement;
  hexHistory: HexHistoryOverlay;
}

export function mountWorld(deps: MountWorldDeps): () => void {
  const {
    appEl, partyId, boot, ws, chat, banner, pauseBanner, isPaused, selfCharId, charNames, npcNames,
    onSelfHpChange, onCombatChange, hexHistory,
  } = deps;

  let scene: SceneMount | null = null;
  let unbindGameWS: (() => void) | null = null;
  let destroyScene: (() => void) | null = null;
  const isRoomGraph = ROOM_GRAPH_KINDS.has(boot.world.kind);

  if (isRoomGraph) {
    const roomWorld = mountRoomWorld({ appEl, boot, ws, isPaused, selfCharId, charNames });
    scene = roomWorld.scene;
    destroyScene = roomWorld.destroy;

    unbindGameWS = bindGameWS({
      ws, chat, banner, pauseBanner, scene,
      selfCharId,
      charNames,
      npcNames,
      onSelfHpChange,
      onCombatChange,
    });
  } else {
    void (async () => {
      const hexWorld = await mountHexWorld({ appEl, partyId, boot, ws, chat, banner, isPaused, selfCharId, hexHistory });
      scene = hexWorld.scene;
      destroyScene = hexWorld.destroy;

      unbindGameWS = bindGameWS({
        ws, chat, banner, pauseBanner, scene,
        selfCharId,
        charNames,
        npcNames,
        onSelfHpChange,
        onCombatChange,
      });
    })();
  }

  const ui = installWorldUi({
    boot,
    ws,
    isPaused,
    onEnter: (locationId) => {
      if (locationId == null || isPaused()) return;
      ws.send('player.enter.location', { location_id: locationId });
    },
  });

  if (boot.character) {
    ui.updateSelfPosition(boot.character.current_q, boot.character.current_r);
  }

  const onCharacterMoved = (msg: { character_id: number; q: number; r: number }): void => {
    if (msg.character_id !== selfCharId) return;
    ui.updateSelfPosition(msg.q, msg.r);
  };
  ws.on('character.moved', onCharacterMoved);

  const onOtherWorldChanged = (msg: { character_id: number; new_world_id: number; q: number; r: number }): void => {
    if (msg.character_id === selfCharId) return;
    const name = charNames.get(msg.character_id) ?? `character ${msg.character_id}`;
    if (msg.new_world_id === boot.world.id) {
      scene?.addCharacter({ id: msg.character_id, name, q: msg.q, r: msg.r, isSelf: false });
    } else {
      scene?.removeCharacter(msg.character_id);
    }
  };
  ws.on('character.world.changed', onOtherWorldChanged);

  return () => {
    unbindGameWS?.();
    unbindGameWS = null;
    destroyScene?.();
    destroyScene = null;
    ws.off('character.moved', onCharacterMoved);
    ws.off('character.world.changed', onOtherWorldChanged);
    ui.destroy();
  };
}
